/**
 * ============================================================
 * KEJJA OS — src/pages/calendar/EventForm.tsx
 * Modal/panel for creating + editing events.
 * ============================================================
 */

import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { X, Trash2, Repeat, MapPin, AlignLeft, Bell, Clock } from 'lucide-react';

import {
  useCalendars, useEvent, useCreateEvent, useUpdateEvent, useDeleteEvent,
} from '@/hooks/useCalendar';
import type { EventRecurrence } from '@/lib/calendarApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface EventFormProps {
  homeId: string;
  eventId: string | null;
  hint: { date?: string; allDay?: boolean } | null;
  onClose: () => void;
  onEditRequest?: (id: string) => void;
}

interface FormState {
  calendarId: string;
  title: string;
  description: string;
  location: string;
  date: string;                   // YYYY-MM-DD
  time: string;                   // HH:MM (24h)
  endDate: string;
  endTime: string;
  allDay: boolean;
  isReminder: boolean;
  recurrence: EventRecurrence;
  recurrenceDays: number[];
  recurrenceUntilDate: string;
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function mkState(hint: { date?: string; allDay?: boolean } | null): FormState {
  return {
    calendarId: '',
    title: '',
    description: '',
    location: '',
    date: hint?.date ?? todayStr(),
    time: '09:00',
    endDate: hint?.date ?? todayStr(),
    endTime: '10:00',
    allDay: hint?.allDay ?? false,
    isReminder: false,
    recurrence: 'none',
    recurrenceDays: [],
    recurrenceUntilDate: '',
  };
}

export function EventForm({ homeId, eventId, hint, onClose }: EventFormProps) {
  const { data: calendarsData } = useCalendars(homeId);
  const { data: eventData } = useEvent(homeId, eventId ?? undefined);
  const createMut = useCreateEvent(homeId);
  const updateMut = useUpdateEvent(homeId);
  const deleteMut = useDeleteEvent(homeId);

  const [form, setForm] = useState<FormState>(() => mkState(hint));
  const [err, setErr] = useState<string | null>(null);

  // When editing, populate form from server data
  useEffect(() => {
    if (eventData?.event) {
      const ev = eventData.event;
      const starts = new Date(ev.startsAt);
      const ends = ev.endsAt ? new Date(ev.endsAt) : null;
      setForm({
        calendarId: ev.calendarId,
        title: ev.title,
        description: ev.description ?? '',
        location: ev.location ?? '',
        date: starts.toISOString().slice(0, 10),
        time: starts.toISOString().slice(11, 16),
        endDate: (ends ?? starts).toISOString().slice(0, 10),
        endTime: (ends ?? starts).toISOString().slice(11, 16),
        allDay: ev.allDay,
        isReminder: ev.isReminder,
        recurrence: ev.recurrence,
        recurrenceDays: ev.recurrenceDays ?? [],
        recurrenceUntilDate: ev.recurrenceUntilDate ?? '',
      });
    }
  }, [eventData]);

  // Default to first calendar if not yet set
  useEffect(() => {
    if (!form.calendarId && calendarsData?.calendars?.[0]) {
      setForm((f) => ({ ...f, calendarId: calendarsData.calendars[0]!.id }));
    }
  }, [calendarsData, form.calendarId]);

  const calendars = calendarsData?.calendars ?? [];

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!form.title.trim()) { setErr('Title is required.'); return; }
    if (!form.calendarId) { setErr('Pick a calendar.'); return; }

    // Build ISO datetimes
    const startsAt = form.allDay
      ? new Date(form.date + 'T00:00:00Z').toISOString()
      : new Date(form.date + 'T' + form.time + ':00Z').toISOString();

    const endsAt = form.allDay
      ? null
      : new Date(form.endDate + 'T' + form.endTime + ':00Z').toISOString();

    const payload = {
      calendarId: form.calendarId,
      title: form.title.trim(),
      description: form.description.trim() || null,
      location: form.location.trim() || null,
      startsAt,
      endsAt,
      allDay: form.allDay,
      isReminder: form.isReminder,
      recurrence: form.recurrence,
      recurrenceDays: form.recurrence === 'custom_weekdays' ? form.recurrenceDays : [],
      recurrenceUntilDate: form.recurrence !== 'none' && form.recurrenceUntilDate
        ? form.recurrenceUntilDate : null,
    };

    const onDone = () => onClose();
    const onError = (e: Error) => setErr(e.message ?? 'Could not save.');

    if (eventId) {
      updateMut.mutate({ eventId, patch: payload }, { onSuccess: onDone, onError });
    } else {
      createMut.mutate(payload, { onSuccess: onDone, onError });
    }
  };

  const handleDelete = () => {
    if (!eventId) return;
    if (!confirm('Delete this event? This will also remove all future occurrences.')) return;
    deleteMut.mutate(eventId, { onSuccess: onClose });
  };

  const toggleDow = (d: number) => {
    const next = form.recurrenceDays.includes(d)
      ? form.recurrenceDays.filter((x) => x !== d)
      : [...form.recurrenceDays, d].sort();
    setForm({ ...form, recurrenceDays: next });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="w-full max-w-lg my-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
        <CardContent className="p-5">
          <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">{eventId ? 'Edit event' : 'New event'}</h2>
              <Button type="button" size="sm" variant="ghost" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Title */}
            <div>
              <Input
                autoFocus
                value={form.title}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, title: e.target.value })}
                placeholder="Event title (e.g. Doctor's appointment)"
                className="text-base"
              />
            </div>

            {/* Calendar picker */}
            <div>
              <Label className="text-xs">Calendar</Label>
              <div className="flex gap-1.5 flex-wrap mt-1">
                {calendars.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setForm({ ...form, calendarId: c.id })}
                    className={cn(
                      'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all border',
                      form.calendarId === c.id ? 'border-transparent' : 'border-border opacity-60',
                    )}
                    style={form.calendarId === c.id ? {
                      backgroundColor: `${c.color}22`,
                      color: c.color,
                      borderColor: `${c.color}66`,
                    } : undefined}
                  >
                    {c.icon && <span>{c.icon}</span>}
                    <span>{c.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* All day + reminder toggles */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, allDay: !form.allDay })}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-xs font-medium transition-colors',
                  form.allDay
                    ? 'border-indigo-500/60 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300'
                    : 'border-border bg-background text-muted-foreground',
                )}
              >
                <Clock className="w-3.5 h-3.5" /> All day
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, isReminder: !form.isReminder })}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-xs font-medium transition-colors',
                  form.isReminder
                    ? 'border-amber-500/60 bg-amber-500/10 text-amber-700 dark:text-amber-300'
                    : 'border-border bg-background text-muted-foreground',
                )}
              >
                <Bell className="w-3.5 h-3.5" /> Reminder
              </button>
            </div>

            {/* Date & time */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setForm({ ...form, date: e.target.value, endDate: form.endDate < e.target.value ? e.target.value : form.endDate })}
                  className="mt-1"
                />
              </div>
              {!form.allDay && (
                <div>
                  <Label className="text-xs">Time</Label>
                  <Input
                    type="time"
                    value={form.time}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, time: e.target.value })}
                    className="mt-1"
                  />
                </div>
              )}
            </div>

            {/* End date & time (for timed events spanning) */}
            {!form.allDay && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">End date</Label>
                  <Input
                    type="date"
                    value={form.endDate}
                    min={form.date}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, endDate: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">End time</Label>
                  <Input
                    type="time"
                    value={form.endTime}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, endTime: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            {/* Recurrence */}
            <div>
              <Label className="text-xs flex items-center gap-1.5">
                <Repeat className="w-3 h-3" /> Repeat
              </Label>
              <select
                value={form.recurrence}
                onChange={(e) => setForm({ ...form, recurrence: e.target.value as EventRecurrence })}
                className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
              >
                <option value="none">Doesn't repeat</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="custom_weekdays">Custom weekdays</option>
                <option value="monthly">Monthly (same date)</option>
                <option value="yearly">Yearly (birthdays)</option>
              </select>

              {form.recurrence === 'custom_weekdays' && (
                <div className="flex gap-1 mt-2">
                  {DOW_LABELS.map((label, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => toggleDow(i)}
                      className={cn(
                        'flex-1 h-8 rounded text-[11px] font-semibold transition-colors',
                        form.recurrenceDays.includes(i)
                          ? 'bg-indigo-600 text-white'
                          : 'bg-muted text-muted-foreground hover:bg-muted/70',
                      )}
                    >
                      {label[0]}
                    </button>
                  ))}
                </div>
              )}

              {form.recurrence !== 'none' && (
                <div className="mt-2">
                  <Label className="text-[10px]">Until (optional)</Label>
                  <Input
                    type="date"
                    value={form.recurrenceUntilDate}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, recurrenceUntilDate: e.target.value })}
                    className="mt-1 h-8 text-xs"
                  />
                </div>
              )}
            </div>

            {/* Location */}
            <div>
              <Label className="text-xs flex items-center gap-1.5">
                <MapPin className="w-3 h-3" /> Location (optional)
              </Label>
              <Input
                value={form.location}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, location: e.target.value })}
                placeholder="Where?"
                className="mt-1"
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-xs flex items-center gap-1.5">
                <AlignLeft className="w-3 h-3" /> Notes (optional)
              </Label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Details…"
                className="mt-1 w-full min-h-[70px] rounded-md border border-border bg-background p-2 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500"
              />
            </div>

            {err && <div className="text-xs text-rose-600 font-medium">{err}</div>}

            <div className="flex gap-2 pt-2 border-t border-border">
              <Button
                type="submit"
                className="bg-indigo-600 hover:bg-indigo-700 text-white flex-1"
                disabled={createMut.isPending || updateMut.isPending}
              >
                {createMut.isPending || updateMut.isPending
                  ? 'Saving…'
                  : eventId ? 'Save changes' : 'Create event'}
              </Button>
              {eventId && (
                <Button
                  type="button" variant="ghost"
                  className="text-rose-600 hover:text-rose-700"
                  onClick={handleDelete}
                  disabled={deleteMut.isPending}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
